<template>
    <v-app :class="{ 'app--mobile-billing-lock': showAppMobileBillingLock }">
        <v-overlay :value="isChannelCreationBusy" opacity="0.6" z-index="3000">
            <div class="d-flex flex-column align-center">
                <v-progress-circular indeterminate color="mainNew" size="56"></v-progress-circular>
                <div class="mt-4 white--text" style="font-size: 14px; font-weight: 500;">
                    Выполняется создание канала и настройка приоритетов…
                </div>
            </div>
        </v-overlay>
        <!--Диалоговые окна-->
        <v-dialog :value="loading && !isChannelCreationBusy" fullscreen :transition="false">
            <v-container fluid fill-height style="background-color: rgba(255, 255, 255, 0.5)">
                <v-layout justify-center align-center>
                    <v-progress-circular indeterminate color="mainNew"> </v-progress-circular>
                </v-layout>
            </v-container>
        </v-dialog>
        <v-snackbar v-model="isError" left :timeout="-1" color="mainNew">
            <span class="white--text">{{ error }}</span>
            <template v-slot:action="{ attrs }">
                <v-btn color="white" text v-bind="attrs" @click="closeError"> Закрыть </v-btn>
            </template>
        </v-snackbar>
        <UniversalModal></UniversalModal>
        <RenameProjectDialog></RenameProjectDialog>
        <HintSidebar></HintSidebar>
        <ConnectorSettingsSidebar></ConnectorSettingsSidebar>
        <AuthDialog></AuthDialog>
        <!-- Уводим со сторонних провайдеров: показывать или нет — решает бэк. -->
        <ReconnectMigrationModal v-if="isProjectPath && project" :project="project" />
        <!-- Анонс вебинара 12.10: всем залогиненным, кроме публичных страниц. -->
        <WebinarAnnouncementModal v-if="!isUnprotectedRoute && !isFullBleedPublicPage" />
        <Notify></Notify>
        <!--Диалоговые окна-->
        <AppTopbar
            v-if="!isFullBleedPublicPage && !hideAppTopbar"
            :is-projects-loading="isProjectsLoading"
            :is-project-path="isProjectPath"
            @login="login"
        />
        <v-main v-if="!isProjectsLoading || isUnprotectedRoute"
            :class="{
            'landing-main': $route.path === '/landing',
            'full': $route.path !== '/landing',
            }"
        >
            <GlobalNotification v-if="!isUnprotectedRoute && !showAppMobileBillingLock" />
            <ChatPopupNotifications v-if="!isUnprotectedRoute && !showAppMobileBillingLock" />
            <ConfirmationDialog />
            <router-view class="router-view"></router-view>
        </v-main>
        <v-container v-else fluid fill-height class="full-height-main header-margin" style="background-color: rgba(255, 255, 255, 0.5)">
            <v-layout justify-center align-center>
                <v-progress-circular indeterminate color="mainNew"> </v-progress-circular>
            </v-layout>
        </v-container>
        <div
            v-if="showAppMobileBillingLock"
            class="app-mobile-billing-lock"
        >
            <div class="app-mobile-billing-lock__blur" aria-hidden="true" />
            <DemoSubView
                overlay
                :notice-goal="mobileBillingLockNoticeGoal"
            />
        </div>
        <div v-if="!isFullBleedPublicPage" class="app-footer-wrap">
            <Footer></Footer>
        </div>
    </v-app>
</template>

<script>
import GlobalNotification from '@/components/GlobalNotification'
import { isInNetworkChatScope, loadNetworkChatScope } from '@/helpers/gcb2/networkChatScope'
import HintSidebar from '@/components/HintSidebar'
import ConnectorSettingsSidebar from '@/components/project/ConnectorSettingsSidebar.vue'
import Notify from '@/components/Notify'
import RenameProjectDialog from '@/components/project/dialogs/RenameProjectDialog'
import ReconnectMigrationModal from '@/components/channels/ReconnectMigrationModal.vue'
import WebinarAnnouncementModal from '@/components/announcements/WebinarAnnouncementModal.vue'
import { useNotificationStore } from '@/store/stores/notification'
import { useChatNotificationsStore } from '@/store/stores/chatNotifications'
import { useMessengerStore } from '@/store/stores/messenger'
import { useBillingStore } from '@/store/stores/billing'
import { useMarketplaceStore } from '@/store/stores/marketplace'
import websockets from '@/websockets'
import { mapStores } from 'pinia'
import qs from 'query-string'
import { mapState, mapGetters } from 'vuex'
import cfg from '../config'
import { getInstance } from './auth/authWrapper'
import ConfirmationDialog from './components/ConfirmationDialog.vue'
import Footer from './components/Footer.vue'
import UniversalModal from './components/UniversalModal/UniversalModal.vue'
import AuthDialog from './components/landing/AuthDialog.vue'
import AppTopbar from './components/AppTopbar.vue'
import DemoSubView from '@/views/GCB2/subViews/DemoSubView.vue'
import { unprotectedRoutesNames } from './router'
import { lockBodyScroll, unlockBodyScroll } from '@/helpers/lockBodyScroll'
import { useAppMobileBillingLock } from '@/hooks/useAppMobileBillingLock'
import { defineFavicon, reloadPageOnIdle } from './utils'
import ChatPopupNotifications from '@/components/ChatPopupNotifications.vue'
import { shouldLoadAllProjectsForProjectsRoute } from '@/helpers/projectListLoad'
import { identifyUser } from '@/helpers/ymLogger'
import { installProjectRealtime } from '@/helpers/projectRealtime'
import { checkRole } from '@/helpers/roles'
import { hasGcb2ChatViewAccess, hasGcb2ChatConnector, isGcb2ChatOpenInRoute, isGcb2ChatPopupEnabled } from '@/helpers/gcb2/chatAccess'
import { tabNotification } from '@/helpers/tabNotification'
import { resolveAuthenticatedLandingRedirect } from '@/helpers/promo/session'

export default {
    name: 'App',

    components: {
        HintSidebar,
        ConnectorSettingsSidebar,
        Notify,
        RenameProjectDialog,
        ReconnectMigrationModal,
        WebinarAnnouncementModal,
        Footer,
        GlobalNotification,
        AuthDialog,
        ConfirmationDialog,
        UniversalModal,
        ChatPopupNotifications,
        AppTopbar,
        DemoSubView,
    },
    setup() {
        const { showAppMobileBillingLock } = useAppMobileBillingLock()
        return { showAppMobileBillingLock }
    },

    data: () => ({
        isNewYear: false,
        path: null,
        cfg,
        authLoadingUnwatch: null,
        authIsAuthenticatedUnwatch: null,
        messageSocketHandler: null,
        dhChatSocketHandler: null,
        dhChatMessageRemovedSocketHandler: null,
        isInitialDataLoaded: false,
        tariffPreloadInFlightByProject: {},
        integrationsPreloadInFlightByProject: {},
        appBillingScrollLocked: false,
    }),
    computed: {
        ...mapState(['loading', 'error', 'isProjectsLoading', 'hideAppTopbar']),
        ...mapState('project', ['areAllProjectsLoaded']),
        ...mapStores(useNotificationStore),
        ...mapGetters(['projectType', 'networkProjects', 'pseudoNetworkProjects']),
        isChannelCreationBusy() {
            return (
                !!this.$store.state.channelCreationMode &&
                (this.$store.state.channelCreationLockCount || 0) > 0
            )
        },
        error() {
            return this.$store.getters.error
        },
        isError() {
            return this.error ? true : false
        },

        isProjectsPath: function() {
            return this.path === '/projects'
        },
        isProjectPath() {
            if (!this.path) {
                return false
            }
            return this.path.split('/')[1] === 'project'
        },
        isUnprotectedRoute() {
            return unprotectedRoutesNames.includes(this.$route.name) || this.isGuestPaymentLink
        },
        // Гость на странице оплаты по ссылке: проекты для него не грузятся, ждать их нельзя.
        isGuestPaymentLink() {
            return this.$route.name === 'SpecialPayment' &&
                Boolean(this.$route.query.t) &&
                !this.$auth?.isAuthenticated
        },
        isFullBleedPublicPage() {
            return this.$route.path === '/landing' ||
                this.$route.path === '/reset-password' ||
                this.$route.name === 'SpecialPayment'
        },
        project() {
            return this.$store.getters.projectById(this.$router.currentRoute.params.id)
        },
        mobileBillingLockNoticeGoal() {
            if (this.$route.query.section === 'chat') {
                return 'чтобы продолжить пользоваться системой.'
            }
            return 'чтобы каналы не отключились.'
        },
    },
    //Add listener for billing balance
    watch: {
        async $route(to, from) {
            this.$nextTick(() => this.updateTopbarHeightVar())
            this.path = to.path
            // Return paths are consumed only after authentication. Without this guard,
            // an unauthenticated visitor could be pushed back to a protected route
            // simply by following the `?to=` query generated by the router.
            // Guest MP init (`/?to=/init/yClients`) is handled in the router guard.
            if (to.query.to && getInstance().isAuthenticated) {
                const currentQuery = this.$router.currentRoute.query
                const toQuery = qs.parseUrl(to.query.to).query
                const path = qs.stringifyUrl({
                    url: to.query.to,
                    query: Object.assign({}, currentQuery, toQuery),
                })
                this.$router.push(path)
            }
            this.redirectIfNoProjectPermission()
            this.redirectToOnboardingIfActive()

            if (this.shouldLoadAllProjectsForProjectsRoute(to)) {
                await this.$store.dispatch('project/setProjects')
            }

            const newProjectId = to.params?.id
            const oldProjectId = from.params?.id
            const newProject = newProjectId ? this.$store.getters.projectById(newProjectId) : null

            if (oldProjectId && oldProjectId !== newProjectId) {
                websockets.leaveProject(oldProjectId)
            }

            if (newProjectId && newProjectId !== oldProjectId) {
                websockets.joinProject(newProjectId)
            }

            await Promise.all([
                this.preloadProjectTariff(newProjectId, newProject),
                this.preloadProjectIntegrations(newProjectId, newProject),
            ])
        },
        isProjectsLoading(isLoading) {
            this.$nextTick(() => this.updateTopbarHeightVar())
            this.redirectIfNoProjectPermission()
            this.redirectToOnboardingIfActive()
            if (isLoading) return
            const projectId = this.$route?.params?.id
            if (!projectId) return
            // Повтор, если вотчер роута успел вызвать preload до появления проекта в сторе.
            this.preloadProjectTariff(projectId)
            this.preloadProjectIntegrations(projectId)
        },
        hideAppTopbar() {
            this.$nextTick(() => this.updateTopbarHeightVar())
        },
        showAppMobileBillingLock: {
            immediate: true,
            handler(locked) {
                this.syncAppMobileBillingLock(Boolean(locked))
            },
        },
        async isProjectPath(newVal) {
            if (newVal) {
                if (!this.projectType) {
                    await Promise.all([
                        this.$store.dispatch('project/setProjectType', {
                            projectId: this.$router.currentRoute.params.id,
                        }),
                        this.$store.dispatch('project/setNetworkProjects', {
                            projectId: this.$router.currentRoute.params.id,
                        }),
                    ])
                    if (this.projectType === 'pseudo_network') {
                        await this.$store.dispatch('project/setPseudoNetworkProjects', {
                            projectId: this.$router.currentRoute.params.id,
                        })
                    }
                }
            } else {
                if (this.projectType) {
                    this.$store.commit('setProjectType', '')
                    this.$store.commit('setNetworkProjects', null)
                    this.$store.commit('setPseudoNetworkProjects', null)
                }
            }
        },
    },
    methods: {
        async login() {
            this.$store.commit('setAuthDialogStartSection', 'login')
            this.$store.commit('setIsAuthDialog', true)
        },

        closeError() {
            this.$store.commit('setError', null)
        },
        syncAppMobileBillingLock(locked) {
            const next = Boolean(locked)
            if (next) {
                document.documentElement.style.setProperty('--app-notification-height', '0px')
            }
            if (next === this.appBillingScrollLocked) return
            this.appBillingScrollLocked = next
            if (next) lockBodyScroll()
            else unlockBodyScroll()
        },
        updateTopbarHeightVar() {
            const topbarEl = this.$el?.querySelector?.('.main-app-bar')
            if (!topbarEl || this.hideAppTopbar) {
                document.documentElement.style.setProperty('--app-topbar-height', '0px')
                document.documentElement.style.setProperty('--app-topbar-extra-height', '0px')
                return
            }
            const height = topbarEl.offsetHeight || 56
            document.documentElement.style.setProperty('--app-topbar-height', `${height}px`)
            document.documentElement.style.setProperty(
                '--app-topbar-extra-height',
                `${Math.max(height - 56, 0)}px`
            )
        },
        shouldLoadAllProjectsForProjectsRoute(to) {
            return shouldLoadAllProjectsForProjectsRoute({
                toPath: to.path,
                isAuthenticated: this.$auth.isAuthenticated,
                isInitialDataLoaded: this.isInitialDataLoaded,
                areAllProjectsLoaded: this.areAllProjectsLoaded,
                isProjectsLoading: this.isProjectsLoading,
            })
        },
        async preloadProjectTariff(projectId, projectArg = null) {
            if (!projectId || !this.$auth?.isAuthenticated) {
                return
            }

            const project = projectArg || this.$store.getters.projectById(projectId)
            if (!project?.billingEnabled) {
                return
            }

            const billingStore = useBillingStore()
            if (billingStore.isCurrentTariffFetched(projectId)) {
                return
            }

            if (this.tariffPreloadInFlightByProject[projectId]) {
                return
            }

            this.$set(this.tariffPreloadInFlightByProject, projectId, true)
            try {
                await billingStore.fetchCurrentTariff(projectId)
            } finally {
                this.$set(this.tariffPreloadInFlightByProject, projectId, false)
            }
        },
        async preloadProjectIntegrations(projectId, projectArg = null) {
            if (!projectId || !this.$auth?.isAuthenticated) {
                return
            }

            const project = projectArg || this.$store.getters.projectById(projectId)
            if (!project?.billingEnabled) {
                return
            }

            const marketplaceStore = useMarketplaceStore()
            if (marketplaceStore.allIntgrations(projectId).length > 0) {
                return
            }

            if (this.integrationsPreloadInFlightByProject[projectId]) {
                return
            }

            this.$set(this.integrationsPreloadInFlightByProject, projectId, true)
            try {
                await marketplaceStore.fetchIntegrations(projectId)
            } finally {
                this.$set(this.integrationsPreloadInFlightByProject, projectId, false)
            }
        },
        setCachedItems() {
            const currentAddingConnectorCredentials = sessionStorage.getItem(
                'currentAddingConnectorCredentials'
            )
            if (
                currentAddingConnectorCredentials !== 'undefined' &&
                currentAddingConnectorCredentials !== null
            ) {
                this.$store.commit(
                    'project/setAddConectorCredentials',
                    JSON.parse(currentAddingConnectorCredentials)
                )
            }

            const currentAddingConnector = sessionStorage.getItem('currentAddingConnector')
            if (currentAddingConnector !== 'undefined' && currentAddingConnector !== null) {
                this.$store.commit('project/updateCurrentAddingConnector', JSON.parse(currentAddingConnector))
            }

            const addConectorSettings = sessionStorage.getItem('currentAddingConnectorSettings')
            if (addConectorSettings !== 'undefined' && addConectorSettings !== null) {
                this.$store.commit('project/setAddConectorSettings', JSON.parse(addConectorSettings))
            }

            const loggedAddingConnector = sessionStorage.getItem('loggedAddingConnector')
            if (loggedAddingConnector !== 'undefined' && loggedAddingConnector !== null) {
                this.$store.commit('project/setLoggedAddingConnector', JSON.parse(loggedAddingConnector))
            }
        },
        async setInitialData(authInstance) {
            identifyUser(authInstance.user)
            const loadStartedAt = Date.now()
            this.$store.commit('setIsProjectsLoading', true)
            let selectedLoadMode = 'legacy-all-projects'
            let resolvedProjectId = null
            try {
                const pathProjectMatch =
                    typeof window !== 'undefined'
                        ? window.location.pathname.match(/^\/project\/([^/]+)/)
                        : null
                resolvedProjectId =
                    this.$route?.params?.id ||
                    this.$router.currentRoute?.params?.id ||
                    this.$router.currentRoute?.path?.split('/')[2] ||
                    (pathProjectMatch && pathProjectMatch[1]) ||
                    null
                // Если в URL есть проект и это первая инициализация — грузим только его (byId),
                // а не весь список. Полный список грузится лениво там, где реально нужен.
                const fastEnabled = !this.isInitialDataLoaded && !!resolvedProjectId
                if (fastEnabled) {
                    selectedLoadMode = 'fast-single-project'
                    await this.$store.dispatch('project/setFastProjectById', resolvedProjectId)
                } else {
                    await this.$store.dispatch('project/setProjects')
                }
            } catch (error) {
                selectedLoadMode = 'fallback-legacy-all-projects'
                await this.$store.dispatch('project/setProjects')
            }
            const loadEndedAt = Date.now()
            console.info(
                JSON.stringify({
                    metric: 'project-initial-load',
                    mode: String(selectedLoadMode),
                    projectId: String(this.$router.currentRoute.params?.id || ''),
                    elapsedMs: String(loadEndedAt - loadStartedAt),
                    timestamp: String(loadEndedAt),
                })
            )
            const token = authInstance.getTokenSilently()
            websockets.connect(token)
            if (this.messageSocketHandler) {
                websockets.socket.off('MESSAGE', this.messageSocketHandler)
            }
            this.messageSocketHandler = payload => {
                this.$store.dispatch('callNotify', payload.message)
            }
            websockets.socket.on('MESSAGE', this.messageSocketHandler)
            installProjectRealtime(this.$store, () => this.$router.currentRoute.params?.id)
            this.setupDhChatSocketHandler()

            // currentRoute на старте может быть ещё без params, хотя проект уже взят из URL.
            const pathProjectId =
                typeof window !== 'undefined'
                    ? (window.location.pathname.match(/^\/project\/([^/]+)/) || [])[1]
                    : null
            const currentProjectId =
                this.$route?.params?.id ||
                this.$router.currentRoute.params?.id ||
                pathProjectId ||
                null
            const currentProject = currentProjectId ? this.$store.getters.projectById(currentProjectId) : null
            if (currentProjectId) {
                websockets.joinProject(currentProjectId)
                await Promise.all([
                    this.preloadProjectTariff(currentProjectId, currentProject),
                    this.preloadProjectIntegrations(currentProjectId, currentProject),
                ])
            }

            this.$store.commit('setIsProjectsLoading', false)
            this.isInitialDataLoaded = true
        },
        handleInvite() {
            setTimeout(() => {
                const authInstance = getInstance()
                if (this.$router.currentRoute.query.action === 'invite') {
                    if (authInstance.isAuthenticated) {
                        this.$router.push(this.$route.path)
                    } else {
                        //Если пользователь с таким email существует, отображаем login
                        if (this.$router.currentRoute.query.user_exists === 'true') {
                            this.$auth.loginWithRedirect()
                            //Если пользователь с таким email не существует, отображаем signup
                        } else {
                            this.$auth.loginWithRedirect({ screen_hint: 'signup' })
                        }
                    }
                }
            }, 500)
        },

        //Редирект на страницу проектов, если нет доступа к проекту
        redirectIfNoProjectPermission() {
            if (this.$route.name === 'SpecialPayment') {
                return
            }
            if (this.isProjectPath && !this.isProjectsLoading) {
                const project = this.$store.getters.projectById(this.$router.currentRoute.params?.id)
                if (!project) {
                    this.$store.dispatch('callNotify', 'Нет прав')
                    this.$router.push('/projects')
                }
            }
        },
        // Пока у проекта isOnBoardingActive, любая его страница, кроме онбординга, ведёт в онбординг.
        // Ждём загрузку проекта: на прямом заходе флаг появляется только после setFastProjectById.
        redirectToOnboardingIfActive() {
            if (this.isProjectsLoading) return
            if (cfg.adminsWhitelist.includes(this.$auth?.user?.sub)) return
            const route = this.$route
            const projectId = route?.params?.id
            if (!projectId) return
            const path = route.path || ''
            const segments = path.split('/')
            if (segments[1] !== 'project') return
            // Роут объявлен как onBoarding, но матчится без учёта регистра: /onboarding тоже онбординг.
            if ((segments[3] || '').toLowerCase() === 'onboarding') return
            const project = this.$store.getters.projectById(projectId)
            if (!project?.isOnBoardingActive) return
            this.$router.replace(`/project/${projectId}/onBoarding`)
        },
        hasDhChatAccess(project) {
            return hasGcb2ChatViewAccess(project) && hasGcb2ChatConnector(project)
        },
        isChatPopupEnabled(project) {
            return isGcb2ChatPopupEnabled(project)
        },
        async normalizeNetworkChatEvent(payload) {
            const projectId = payload?.projectId
            const currentProjectId = this.$router.currentRoute.params?.id
            if (!projectId || !currentProjectId || projectId === currentProjectId) {
                return payload
            }
            const scope = await loadNetworkChatScope(currentProjectId)
            if (!isInNetworkChatScope(scope, currentProjectId, projectId)) {
                return payload
            }
            return { ...payload, projectId: currentProjectId, ownerProjectId: projectId }
        },
        setupDhChatSocketHandler() {
            if (this.dhChatSocketHandler) {
                websockets.socket.off('DH_CHAT_MESSAGE_UPSERTED', this.dhChatSocketHandler)
            }
            if (this.dhChatMessageRemovedSocketHandler) {
                websockets.socket.off(
                    'DH_CHAT_MESSAGE_REMOVED',
                    this.dhChatMessageRemovedSocketHandler
                )
            }

            this.dhChatSocketHandler = async payload => {
                try {
                    // Сетевой режим: на main приходят события филиалов с их
                    // projectId. Дальше событие идёт как событие main —
                    // владельца чата BFF найдёт сам по chatId.
                    payload = await this.normalizeNetworkChatEvent(payload)
                    const projectId = payload?.projectId
                    const currentProjectId = this.$router.currentRoute.params?.id
                    if (!projectId || projectId !== currentProjectId) {
                        return
                    }

                    const project = this.$store.getters.projectById(projectId)
                    if (!this.hasDhChatAccess(project)) {
                        return
                    }

                    const messengerStore = useMessengerStore()
                    messengerStore.setProjectId(projectId)
                    messengerStore.setOmnichannel(true)

                    const route = this.$router.currentRoute
                    const result = await messengerStore.handleDhChatMessageUpserted(payload, {
                        isChatVisible: (chatId) => isGcb2ChatOpenInRoute(route, chatId),
                    })
                    if (!result?.shouldNotify || !this.isChatPopupEnabled(project)) {
                        return
                    }

                    const title =
                        result.chat.name ||
                        result.snapshot.chat.clientName ||
                        result.snapshot.chat.phone ||
                        'Новое сообщение'
                    const text = result.message.text || 'Новое сообщение'

                    useChatNotificationsStore().push({
                        projectId,
                        chatId: payload.chatId,
                        messageId: payload.messageId,
                        title,
                        text,
                        provider:
                            result.chat.provider ||
                            result.snapshot.chat.provider ||
                            result.message.appId,
                        timestamp: result.message.timestamp || result.snapshot.message.createdAt,
                    })

                    // Если вкладка в фоне — мигаем заголовком «Новое сообщение»
                    tabNotification.flashNewMessage()
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('[dh-chat][realtime] failed to handle DH_CHAT_MESSAGE_UPSERTED', {
                        projectId: payload?.projectId,
                        chatId: payload?.chatId,
                        messageId: payload?.messageId,
                        err,
                    })
                }
            }

            websockets.socket.on('DH_CHAT_MESSAGE_UPSERTED', this.dhChatSocketHandler)

            this.dhChatMessageRemovedSocketHandler = async payload => {
                try {
                    // Сетевой режим: на main приходят события филиалов с их
                    // projectId. Дальше событие идёт как событие main —
                    // владельца чата BFF найдёт сам по chatId.
                    payload = await this.normalizeNetworkChatEvent(payload)
                    const projectId = payload?.projectId
                    const currentProjectId = this.$router.currentRoute.params?.id
                    if (!projectId || projectId !== currentProjectId) {
                        return
                    }

                    const project = this.$store.getters.projectById(projectId)
                    if (!this.hasDhChatAccess(project)) {
                        return
                    }

                    const messengerStore = useMessengerStore()
                    messengerStore.setProjectId(projectId)
                    messengerStore.setOmnichannel(true)
                    await messengerStore.handleDhChatMessageRemoved(payload)
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('[dh-chat][realtime] failed to handle DH_CHAT_MESSAGE_REMOVED', {
                        projectId: payload?.projectId,
                        chatId: payload?.chatId,
                        messageId: payload?.messageId,
                        err,
                    })
                }
            }

            websockets.socket.on(
                'DH_CHAT_MESSAGE_REMOVED',
                this.dhChatMessageRemovedSocketHandler
            )
        },
    },
    created: async function() {
        defineFavicon()
        const authInstance = getInstance()
        this.authLoadingUnwatch = authInstance.$watch('loading', async loading => {
            if (!loading) {
                this.path = this.$router.currentRoute.path
                this.handleInvite()
                if (authInstance.isAuthenticated) {
                    this.setInitialData(authInstance)
                }
            }
        })
        this.authIsAuthenticatedUnwatch = authInstance.$watch('isAuthenticated', async isAuthenticated => {
            if (!authInstance.loading) {
                if (isAuthenticated) {
                    const routePath = this.$router.currentRoute.path
                    if (routePath === '/' || routePath === '/landing') {
                        const nextPath = resolveAuthenticatedLandingRedirect(
                            this.$router.currentRoute.query
                        )
                        this.$router.push(nextPath)
                    }
                    this.setInitialData(authInstance)
                } else {
                    const path = qs.stringifyUrl({
                        url: '/',
                        query: this.$router.currentRoute.query,
                    })
                    this.$router.push(path)
                }
            }
        })
        this.setCachedItems()
    },
    mounted() {
        this.updateTopbarHeightVar()
        window.addEventListener('resize', this.updateTopbarHeightVar)
        reloadPageOnIdle(cfg.idleReloadTimeout)
    },
    beforeDestroy() {
        this.syncAppMobileBillingLock(false)
        window.removeEventListener('resize', this.updateTopbarHeightVar)
        if (this.authLoadingUnwatch) {
            this.authLoadingUnwatch()
            this.authLoadingUnwatch = null
        }
        if (this.authIsAuthenticatedUnwatch) {
            this.authIsAuthenticatedUnwatch()
            this.authIsAuthenticatedUnwatch = null
        }
        if (this.messageSocketHandler) {
            websockets.socket.off('MESSAGE', this.messageSocketHandler)
            this.messageSocketHandler = null
        }
        if (this.dhChatSocketHandler) {
            websockets.socket.off('DH_CHAT_MESSAGE_UPSERTED', this.dhChatSocketHandler)
            this.dhChatSocketHandler = null
        }
        if (this.dhChatMessageRemovedSocketHandler) {
            websockets.socket.off(
                'DH_CHAT_MESSAGE_REMOVED',
                this.dhChatMessageRemovedSocketHandler
            )
            this.dhChatMessageRemovedSocketHandler = null
        }
    },
}
</script>

<style lang="sass">
@import '@/vars.sass'
@import '@/assets/styles/GCB2.sass'

html, body
    width: 100%
    margin: 0
    padding: 0

html, body, .v-application
    font-family: $font-family-base !important
    background-color: $app-bcg-new !important

.v-application,
.v-application--wrap
    width: 100% !important
    max-width: 100% !important
    min-width: 0
    align-items: stretch

.landing-main
    padding: 0 !important
    min-height: 100vh !important

    > .v-main__wrap
        min-height: 100vh
        display: flex
        flex-direction: column

    .router-view
        flex: 1 1 auto
        min-height: 0

.main-no-topbar
    padding-top: 0 !important

.app-footer-wrap
    display: block
    flex: 0 0 auto
    align-self: stretch
    width: 100%
    max-width: 100%
    min-width: 0
    margin: 0
    padding: 0
    box-sizing: border-box

.v-tabs > .v-tabs-bar, .v-tabs-items, .v-data-table, .v-card
    background-color: $app-bcg-new !important
.v-input--selection-controls__input input:checked ~ .v-input--switch__track
    color: $main-new !important
    opacity: .6
.v-input--selection-controls__input input:checked ~ .v-input--switch__thumb
    color: $main-new !important
.main-app-bar.v-app-bar
    background-color: $navy-new !important
    box-shadow: none !important
    background: radial-gradient(circle at 82% 50%, rgba(82, 90, 213, .18) 0, transparent 33%), linear-gradient(180deg, #202239 0%, #1A1A2E 100%) !important
    padding: 0 !important
    overflow: visible !important
    .v-toolbar__content
        padding: 0 !important
        overflow: visible !important

.topbar
    min-height: 56px

.topbar-shell
    width: 100%
    margin: 0 auto
    display: flex
    align-items: center
    justify-content: space-between
    gap: 12px
    padding: 8px 20px

.topbar-left
    flex: 1 1 auto
    min-width: 0
    display: flex
    align-items: center
    gap: 16px
    overflow: visible

.topbar-actions
    flex: 0 0 auto
    display: flex
    align-items: center
    gap: 8px
    white-space: nowrap
    margin-left: 12px

.header-btn
    height: 36px !important
    min-width: auto !important
    border-radius: 10px !important
    font-size: 12.5px !important
    font-weight: 800 !important
    letter-spacing: -.01em
    text-transform: none !important
    transition: transform .15s ease, background .15s ease, border-color .15s ease, filter .15s ease

    &:hover
        transform: translateY(-1px)

    &:focus-visible
        outline: 2px solid #fff
        outline-offset: 2px

.header-btn--secondary
    padding: 0 14px !important
    background: rgba(255, 255, 255, .05) !important
    border: 1px solid rgba(255, 255, 255, .17) !important
    color: #fff !important

    &:hover
        background: rgba(255, 255, 255, .085) !important
        border-color: rgba(255, 255, 255, .26) !important

@media (max-width: 1180px)
    .topbar-shell
        width: 100%
        padding-left: 14px
        padding-right: 14px
    .topbar-left
        gap: 12px

@media (max-width: 980px)
    .topbar-actions
        gap: 6px
    .header-btn--secondary
        min-width: 42px !important
        width: 42px
        padding: 0 !important

// Compact one-row header (matches mockup: no wrap to 2 lines)
@media (max-width: 760px)
    .main-app-bar.v-app-bar
        height: 56px !important
        box-shadow: 0 4px 14px rgba(12, 12, 28, .16) !important
        .v-toolbar__content
            height: 56px !important
            min-height: 56px !important
            overflow: visible !important
    .topbar-shell
        height: 56px
        min-height: 56px
        flex-direction: row
        flex-wrap: nowrap
        align-items: center
        justify-content: space-between
        width: 100%
        gap: 8px
        padding: 8px 10px
    .topbar-left
        flex: 1 1 auto
        width: auto
        min-width: 0
        gap: 10px
        margin-bottom: 0
        overflow: hidden
    .topbar-actions
        flex: 0 0 auto
        width: auto
        justify-content: flex-end
        align-items: center
        flex-wrap: nowrap
        gap: 5px
        margin-left: 0
        overflow: visible
        padding-bottom: 0
    .header-btn
        width: 34px !important
        min-width: 34px !important
        height: 34px !important
        padding: 0 !important
        border-radius: 9px !important
    .header-btn--secondary
        width: 34px !important
        min-width: 34px !important
        padding: 0 !important

@media (max-width: 430px)
    .topbar-shell
        gap: 6px
        padding-left: 8px
        padding-right: 8px
    .topbar-left
        gap: 8px
    .topbar-actions
        gap: 4px

a
    text-decoration: none
h1.dashboard
    font-weight: normal !important
    font-size: 36px !important
h2.dashboard
    font-weight: normal !important
    font-size: 26px !important
h3.dashboard
    font-weight: normal !important
    font-size: 20px !important
header
    z-index: 102 !important
.col
    &.no-padding
        padding: 0
.sidebar-wrapper
    display: flex
    min-width: 0
    max-width: 100%
.content
    padding-inline: 16px
    max-width: 1048px
    flex-grow: 1
    min-width: 0
    margin-top: 24px
    &.sidebar-is-open
        margin-left: 25px
        margin-right: 25px
.v-menu__content
    z-index: 10000 !important
.full
    min-height: 100vh
.full-height-main
    min-height: calc(100vh - var(--app-notification-height, 0px))
.header-margin
    margin-top: calc(var(--app-topbar-extra-height, 0px) + var(--app-notification-height, 0px))
.content-wrapper
    flex-grow: 1
    display: flex
    justify-content: center
    min-width: 0
    max-width: 100%
    margin-bottom: 136px
    margin-top: calc(var(--app-topbar-extra-height, 0px) + var(--app-notification-height, 0px))

.app-mobile-billing-lock
    position: fixed
    top: var(--app-topbar-height, 56px)
    right: 0
    bottom: 0
    left: 0
    z-index: 40
    isolation: isolate
    overflow: hidden
    overscroll-behavior: none
    touch-action: none
    &__blur
        position: absolute
        inset: 0
        z-index: 1
        pointer-events: none
        background: rgba(248, 249, 255, .55)
        -webkit-backdrop-filter: blur(8px)
        backdrop-filter: blur(8px)

.app--mobile-billing-lock
    overflow: hidden
    height: 100%
    max-height: 100vh
    max-height: 100dvh
    .v-main,
    .v-main__wrap,
    .content-wrapper
        overflow: hidden !important
        overscroll-behavior: none
        max-height: 100%
    .content-wrapper
        margin-bottom: 0
    .app-footer-wrap
        pointer-events: none

html.billing-lock-no-scroll,
body.billing-lock-no-scroll
    overflow: hidden !important
    overscroll-behavior: none
    height: 100%
    max-height: 100%
html.billing-lock-no-scroll .v-application,
html.billing-lock-no-scroll .v-main,
html.billing-lock-no-scroll .v-main__wrap,
html.billing-lock-no-scroll .content-wrapper
    overflow: hidden !important
    overscroll-behavior: none
.v-application .headline
    font-size: 20px !important
.nav-btn
    color: $nav-btn-text
    padding-left: 20px
    height: 56px
    width: 180px
    padding-right: 20px
    font-size: 24px
    border-bottom: 3px solid transparent
.nav-btn:focus
    outline: 0
.nav-btn:hover
    border-bottom: 3px solid $main
    background-color: $main-dark-hover
.nav-btn-active
    color: $main
    background-color: $main-dark-hover
    border-bottom: 3px solid $main
.btn-w-icon
    padding-left: 8px !important
.mr-10px
    margin-right: 10px
.mr-5px
    margin-right: 5px
.ml-15px
    margin-left: 15px
.visibility-hidden
    visibility: hidden
.v-dialog
    border-radius: 0
.text-btn:hover
    color: black
.text-btn-wrapper:hover
    background-color: $light-gray-5
    cursor: pointer
.text-btn-wrapper
    font-size: 14px !important
    padding: 0 !important
    &.disabled
        opacity: 0.5
.thin-btn
    border: 1px solid #0000001F
    padding-left: 5px !important
.outlined-btn
    border: 1px solid #0000001F
    .v-btn__loader > .v-progress-circular
        color: $main-new !important
.error--text span
    color: #ff5252 !important
    caret-color: #ff5252 !important
.v-btn__loader > .v-progress-circular
    color: white !important
.error--text div
    color: #ff5252 !important
    caret-color: #ff5252 !important
.v-list-item--disabled .v-list-item__content .v-list-item__title
    color: rgba(0, 0, 0, 0.38) !important
.v-list-item--disabled.text-btn-wrapper  > span
    color: rgba(0, 0, 0, 0.38) !important

h1, h2, h3, h4, h5, h6, p, span, td:not(.metric-value), div:not(.v-alert__wrapper, .v-alert__content, .v-btn__content, .v-input__slot, .v-tab--active, .v-tab, .v-tabs, .v-tabs-bar__content, .v-tabs-slider-wrapper, .v-slide-group__wrapper, .v-input__control, .v-input--selection-controls__input, .clickable-cell, .v-list-item__title)
    color: $text-color
input
    color: $text-color !important
.v-list-item--disabled
    color: rgba(0, 0, 0, 0.38) !important
.v-application .error--text
    color: #ff5252 !important
    caret-color: #ff5252 !important
thead
    background-color: #0000000b
td
    border-bottom: thin solid rgba(0, 0, 0, 0.12)
tr

    font-size: 16px !important
th
    font-weight: 400
    font-size: 16px !important
    cursor: default
code
    background-color: $light-gray !important
    color: black !important
td.text-end
    padding-right: 4px!important
.v-input__prepend-inner
    padding-right: 12px !important
    padding-left: 4px
.footer
    display: flex
    justify-content: center
    align-items: center

    &-content
        width: 1016px
.v-tour__target--highlighted
    z-index: 0
    box-shadow: 0 0 0 0 #2A3B5199 !important
.v-tour__target--highlighted:after
    z-index: -1
    content: ''
    position: absolute
    border-radius: 100%
    width: 100px
    height: 100px
    box-shadow: 0px 0px 0px 666666px #2A3B5199 !important
    sbox-shadow: 0 0 0 666666px rgba(42,59,81,.6)!important
    pointer-events: none
.v-step__arrow
    display: none

.v-tour__target--highlighted.custom-highlight:after
    z-index: -1
    content: ''
    position: absolute
    border-radius: 100%
    width: 210px !important
    height: 210px !important
    transform: translate(-30px, -50px)
    box-shadow: 0px 0px 0px 666666px #2A3B5199 !important
    sbox-shadow: 0 0 0 666666px rgba(42,59,81,.6)!important
    pointer-events: none

.v-window
    overflow: visible !important

.gcb-returnability-subheader-highlight-text
    font-weight: bold
.v-slider__thumb-label span
    color: #fff !important
.text-highlight
    background-color: $main-light !important
    border-radius: 3px !important
.sidebar-input
    max-width: 300px
.sidebar-title
    font-size: 20px
.v-input--reverse .v-input__slot
    flex-direction: row-reverse
    justify-content: flex-end
.svg-icon:hover
    filter: invert(44%) sepia(82%) saturate(2059%) hue-rotate(198deg) brightness(103%) contrast(101%)
.v-text-field--filled > .v-input__control > .v-input__slot
    background: #2A3B510B !important
.lightgray-border
    border: 1px solid #0000001F
.font-size-14px
    font-size: 14px
.theme--light.v-btn
    color: $text-color
.global-alert-tooltip
    opacity: 1 !important
    margin: 0 !important
input, textarea
    color: $text-color
.select-input input
    cursor: pointer
button:not(.white--text) .v-btn__loader svg
    color: $main
button.v-btn--outlined.white--text .v-btn__loader svg
    color: $main
.add-btn
    border: 1px solid $main
    border-radius: 4px
    width: 24px
    height: 24px
    display: flex
    align-items: center
    justify-content: center
    &:hover
      background-color: $main-light
    &_disabled
        border: 1px solid $gray-2
        border-radius: 4px
        width: 24px
        height: 24px
        display: flex
        align-items: center
        justify-content: center
.v-tooltip__content
    background-color: #2A3B51DE !important
    white-space: break-spaces !important
    span
        color: white !important

.add-btn
    border: 1px solid $main
    border-radius: 4px
    width: 24px
    height: 24px
    display: flex
    align-items: center
    justify-content: center
    &.active
        background-color: $main-light
</style>
